import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ChevronLeft } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const DiscoveryPage = () => {
  const [caseType, setCaseType] = useState("personal_injury");
  const [partyRole, setPartyRole] = useState("plaintiff");
  const [checklist, setChecklist] = useState(null);
  const [checked, setChecked] = useState({});
  
  const fetchChecklist = async () => {
    try {
      const res = await axios.post(`${API}/discovery/checklist`, { case_type: caseType, party_role: partyRole });
      setChecklist(res.data.checklist);
      setChecked({});
    } catch (e) {
      console.error(e);
    }
  };
  
  useEffect(() => {
    fetchChecklist();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseType, partyRole]);
  
  const toggle = (section, item) => {
    const key = `${section}-${item}`;
    setChecked(p => ({ ...p, [key]: !p[key] }));
  };
  
  const progress = checklist ? 
    Object.keys(checked).filter(k => checked[k]).length / 
    Object.values(checklist).flat().length * 100 : 0;
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="discovery-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-4xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Discovery Checklist</h1>
          <p className="text-muted-foreground">Case-specific discovery planning</p>
        </div>
        
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px] space-y-1.5">
                <Label>Case Type</Label>
                <Select value={caseType} onValueChange={setCaseType}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="personal_injury">Personal Injury</SelectItem>
                    <SelectItem value="contract">Contract</SelectItem>
                    <SelectItem value="employment">Employment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[200px] space-y-1.5">
                <Label>Party Role</Label>
                <Select value={partyRole} onValueChange={setPartyRole}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="plaintiff">Plaintiff</SelectItem>
                    <SelectItem value="defendant">Defendant</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {checklist && (
              <div className="mt-4">
                <div className="flex justify-between text-sm mb-2">
                  <span>Progress</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <Progress value={progress} />
              </div>
            )}
          </CardContent>
        </Card>
        
        {checklist && Object.entries(checklist).map(([section, items]) => (
          <Card key={section} className="mb-4">
            <CardHeader className="pb-3">
              <CardTitle className="capitalize">{section.replace(/_/g, ' ')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {items.map((item, i) => {
                  const key = `${section}-${item}`;
                  return (
                    <label key={i} className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked[key] || false}
                        onChange={() => toggle(section, item)}
                        className="mt-1 h-4 w-4 rounded border-slate-300"
                      />
                      <span className={checked[key] ? 'line-through text-muted-foreground' : ''}>{item}</span>
                    </label>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <Footer />
    </main>
  );
};


export default DiscoveryPage;
